import { type BattleSummary, defaultBattlesLobbyCopy } from "@loot-os/api-contract";
import type {
  BattleGroupContent,
  BattlesListState,
  BattlesLobbyContent,
  BattlesView,
} from "@loot-os/design-system/templates/battles/battlesContent";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { battleVisualStatus } from "../../../../lib/battleTimeline.js";
import { battlesQueryOptions, myBattlesInfiniteQueryOptions } from "../../../../lib/queries.js";
import { useShopper } from "../../../../lib/shopper.js";
import { useTerminology } from "../../../../lib/terminology.js";
import { useLobbyChannel } from "../../../../lib/useBattleChannel.js";
import { useBattleCard } from "../shared/useBattleCard.js";

/** How many cards show at first, and how many more each See more adds. */
export const LOBBY_INITIAL_VISIBLE = 12;
const REVEAL_STEP = 12;

/** The list's groups, in order, each titled by the status its battles are in. */
const GROUPS = [
  { key: "joinable", status: "created" },
  { key: "in-progress", status: "started" },
  { key: "finished", status: "completed" },
] as const;

/**
 * GET /v1/battles: the lobby, joinable first, then in progress, then finished (faded). A
 * signed-in shopper also sees their own private battles here, tagged; everyone else sees the
 * public ones alone. The lobby is fetched anew once the session token exists (the key changes).
 * "My battles" pages through GET /v1/me/battles. The words are the store's default copy
 * (`defaultBattlesLobbyCopy`; each card is `useBattleCard`'s); the load
 * failure is the platform's.
 */
export function useBattlesLobbyContent(): BattlesLobbyContent {
  const { user, sessionToken } = useShopper();
  const terms = useTerminology();
  const copy = defaultBattlesLobbyCopy(terms);
  const live = useLobbyChannel();
  const [view, setView] = useState<BattlesView>("all");
  const [visible, setVisible] = useState(LOBBY_INITIAL_VISIBLE);

  const lobby = useQuery(
    battlesQueryOptions({ viewer: sessionToken && user ? user.id : "anon", poll: !live }),
  );
  const mine = useInfiniteQuery({
    ...myBattlesInfiniteQueryOptions(),
    enabled: view === "mine" && !!sessionToken,
  });

  const battles: BattleSummary[] =
    view === "mine"
      ? (mine.data?.pages.flatMap((page) => page.data) ?? [])
      : (lobby.data?.data ?? []);

  const card = useBattleCard();

  let budget = visible;
  const groups: BattleGroupContent[] = [];
  for (const group of GROUPS) {
    const shown = battles
      .filter((b) => battleVisualStatus(b) === group.key)
      .slice(0, Math.max(0, budget));
    budget -= shown.length;
    if (shown.length > 0) {
      groups.push({
        key: group.key,
        title: copy.list.groups[group.status],
        cards: shown.map(card),
      });
    }
  }

  const source = view === "mine" ? mine : lobby;
  const state: BattlesListState =
    source.error && battles.length === 0
      ? {
          status: "error",
          message:
            source.error instanceof Error && source.error.message
              ? source.error.message
              : `failed to load ${terms.battle.plural}`,
        }
      : (view === "all" || sessionToken) && source.isPending
        ? { status: "loading" }
        : battles.length === 0
          ? {
              status: "empty",
              message: view === "mine" ? copy.list.empty.mine : copy.list.empty.all,
            }
          : { status: "ready", groups };

  return {
    heading: {
      title: copy.heading.title,
      description: copy.heading.description,
      action: user
        ? { label: copy.heading.create, href: "/battles/create", emphasis: "primary" }
        : { label: copy.heading.signInToCreate, href: "/login", emphasis: "secondary" },
    },
    filter: {
      value: view,
      label: copy.filter.label,
      options: [
        { value: "all", label: copy.filter.options.all },
        { value: "mine", label: copy.filter.options.mine, disabled: !user },
      ],
      onValueChange: (next) => setView(next === "mine" ? "mine" : "all"),
    },
    list: {
      label: copy.list.label,
      state,
      more:
        state.status === "ready" && battles.length > visible
          ? { label: copy.list.more, onMore: () => setVisible((v) => v + REVEAL_STEP) }
          : null,
      loadMore:
        view === "mine" && mine.hasNextPage
          ? {
              label: copy.list.loadMore,
              loading: mine.isFetchingNextPage,
              onLoadMore: () => void mine.fetchNextPage(),
            }
          : null,
    },
  };
}
